import { GraphQLClient } from 'graphql-request';
import { DateTime } from 'luxon';

// GraphQL mutations
const CREATE_EVENT = `
  mutation CreateEvent($payload: ShowCreateInput!) {
    createShow(payload: $payload) {
      id
      name
      domain_name
    }
  }
`;

const CREATE_SESSION = `
  mutation CreateSession($payload: EpisodeCreateInput!) {
    createEpisode(payload: $payload) {
      id
      name
    }
  }
`;

const CREATE_MODULE = `
  mutation CreateModule($payload: ModuleCreateInput!) {
    createModule(payload: $payload) {
      id
      title
      type
      subtype
    }
  }
`;

const CREATE_OPTION = `
  mutation CreateOption($payload: OptionCreateInput!) {
    createOption(payload: $payload) {
      id
      title
      weight
    }
  }
`;

const ARCHIVE_DOMAINS = `
  mutation ArchiveDomains($domain_names: [String!]!) {
    archiveDomains(domain_names: $domain_names) {
      success
      message
    }
  }
`;

class BulkImportApi {
  constructor(token, endpoint = process.env.REACT_APP_API_URL) {
    this.token = token;
    this.client = new GraphQLClient(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  // Helper method to upload image from URL
  async uploadImageFromUrl(imageUrl) {
    if (!imageUrl) return null;

    try {      
      // Use server-side endpoint to handle both regular URLs and Google Drive URLs
      const uploadResponse = await fetch(`${process.env.REACT_APP_REST_URL}/upload-from-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`,
        },
        body: JSON.stringify({ imageUrl }),
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        console.warn(`Failed to upload image: ${errorData.error || uploadResponse.statusText}`);
        return null;
      }

      const result = await uploadResponse.json();
      return result.filename;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  }

  async createEvent(clientId, eventData) {
    try {
      // Upload logo if provided
      let uploadedImage = null;
      if (eventData.logo) {
        uploadedImage = await this.uploadImageFromUrl(eventData.logo);
      }

      const payload = {
        client_id: clientId,
        name: eventData.name,
        domain_name: eventData.domain_name,
        description: eventData.description || '',
        primary_color: eventData.primary_color || '1B1464',
        secondary_color: eventData.secondary_color || '00C9FF',
        display_header: eventData.display_header || false,
        display_menu: eventData.display_menu || false,
        display_qr: eventData.display_qr || false,
        display_info: eventData.display_info || false,
        image: uploadedImage,
        track_user: eventData.track_user || false,
        language: eventData.language || 'EN',
        payment_provider: eventData.payment_provider || 'none',
        start_date: eventData.start_date || null,
        end_date: eventData.end_date || null,
      };

      // Remove any undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const variables = { payload };

      const result = await this.client.request(CREATE_EVENT, variables);
      return result.createShow;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating event:', error);
      
      // Log detailed error information
      if (error.response && error.response.errors) {
        console.error('GraphQL Errors:', JSON.stringify(error.response.errors, null, 2));
      }
      if (error.request) {
        console.error('Request details:', JSON.stringify(error.request, null, 2));
      }
      
      throw error;
    }
  }

  async createSession(eventId, sessionData) {
    try {
      // Upload image if provided
      let uploadedImage = null;
      if (sessionData.image) {
        uploadedImage = await this.uploadImageFromUrl(sessionData.image);
      }

      const payload = {
        show_id: eventId,
        name: sessionData.name,
        description: sessionData.description || '',
        is_live: sessionData.is_live || false,
        show_terms: sessionData.show_terms || false,
        image: uploadedImage,
        display_info: sessionData.display_info || false,
        terms_location: sessionData.terms_location || 'welcome',
      };

      // Remove any undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const variables = { payload };

      const result = await this.client.request(CREATE_SESSION, variables);
      return result.createEpisode;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating session:', error);
      
      // Log detailed error information
      if (error.response && error.response.errors) {
        console.error('GraphQL Errors:', JSON.stringify(error.response.errors, null, 2));
      }
      if (error.request) {
        console.error('Request details:', JSON.stringify(error.request, null, 2));
      }
      
      throw error;
    }
  }

  async createModule(sessionId, moduleData, moduleIndex = 0) {
    try {
      // Upload image if provided
      let uploadedImage = null;
      let uploadedBannerImage = null;
      
      if (moduleData.image) {
        uploadedImage = await this.uploadImageFromUrl(moduleData.image);
      }
      
      if (moduleData.banner_image) {
        uploadedBannerImage = await this.uploadImageFromUrl(moduleData.banner_image);
      }

      // Handle schedule_start and schedule_end
      // The backend API should handle timezone-aware date parsing and conversion
      // But if it fails, we need to handle it here as a fallback
      let schedule_start = moduleData.schedule_start || null;
      let schedule_end = moduleData.schedule_end || null;
      
      // If the dates are still raw strings (backend processing failed), convert to ISO format
      // WITHOUT timezone conversion since that should be handled by the backend
      if (schedule_start && typeof schedule_start === 'string' && !schedule_start.includes('T')) {
        console.warn('Backend date processing failed, converting to ISO format:', schedule_start);
        const parsedDate = DateTime.fromFormat(schedule_start, 'dd/MM/yyyy HH:mm:ss', { zone: 'utc' });
        if (parsedDate.isValid) {
          schedule_start = parsedDate.toISO();
        } else {
          console.error('Failed to parse schedule_start:', schedule_start);
          schedule_start = null;
        }
      }
      
      if (schedule_end && typeof schedule_end === 'string' && !schedule_end.includes('T')) {
        console.warn('Backend date processing failed, converting to ISO format:', schedule_end);
        const parsedDate = DateTime.fromFormat(schedule_end, 'dd/MM/yyyy HH:mm:ss', { zone: 'utc' });
        if (parsedDate.isValid) {
          schedule_end = parsedDate.toISO();
        } else {
          console.error('Failed to parse schedule_end:', schedule_end);
          schedule_end = null;
        }
      }

      const payload = {
        episode_id: sessionId,
        title: moduleData.name,
        subtitle: moduleData.subtitle || '',
        type: moduleData.type || 'vote',
        subtype: moduleData.subtype,
        status: moduleData.status,
        published: moduleData.published || true,
        weight: moduleIndex,
        vote_limit: moduleData.vote_limit || null,
        show_results: moduleData.show_results || false,
        show_total_count: moduleData.show_total_count || false,
        show_all_comments: moduleData.show_all_comments ? (moduleData.show_all_comments === 'Y' || moduleData.show_all_comments === true) : false,
        show_own_comments: moduleData.show_own_comments ? (moduleData.show_own_comments === 'Y' || moduleData.show_own_comments === true) : false,
        show_new_comments: moduleData.show_new_comments !== undefined ? (moduleData.show_new_comments === 'Y' || moduleData.show_new_comments === true) : true,
        is_paid: moduleData.is_paid ? (moduleData.is_paid === 'Y' || moduleData.is_paid === true) : false,
        price: moduleData.price || 0,
        megavote_price: moduleData.megavote_price || 0,
        megavote_amount: moduleData.megavote_amount || 0,
        moodmeter_labels: moduleData.moodmeter_labels || [],
        collect_contact_details: moduleData.collect_contact_details ? (moduleData.collect_contact_details === 'Y' || moduleData.collect_contact_details === true) : false,
        image: uploadedImage,
        banner_image: uploadedBannerImage,
        header_text: moduleData.header_text || null,
        cta_text: moduleData.cta_text || null,
        schedule_start,
        schedule_end,
      };

      // Only include contact_cta_text if collect_contact_details is true and cta_text is provided
      if ((moduleData.collect_contact_details === 'Y' || moduleData.collect_contact_details === true) && moduleData.contact_cta_text) {
        payload.contact_cta_text = moduleData.contact_cta_text;
      }

      // Remove any undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const variables = { payload };

      const result = await this.client.request(CREATE_MODULE, variables);
      return result.createModule;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating module:', error);
      
      // Log detailed error information
      if (error.response && error.response.errors) {
        console.error('GraphQL Errors:', JSON.stringify(error.response.errors, null, 2));
      }
      if (error.request) {
        console.error('Request details:', JSON.stringify(error.request, null, 2));
      }
      
      throw error;
    }
  }

  async createOption(moduleId, optionData) {
    try {
      // Upload image if provided
      let uploadedImage = null;
      if (optionData.image) {
        uploadedImage = await this.uploadImageFromUrl(optionData.image);
      }

      const payload = {
        module_id: moduleId,
        title: optionData.text,
        subtitle: optionData.subText,
        image: uploadedImage,
        url: optionData.url,
        weight: optionData.weight || 0,
        published: optionData.published || true,
      };

      // Remove any undefined values
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const variables = { payload };

      const result = await this.client.request(CREATE_OPTION, variables);
      return result.createOption;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating option:', error);
      
      // Log detailed error information
      if (error.response && error.response.errors) {
        console.error('GraphQL Errors:', JSON.stringify(error.response.errors, null, 2));
      }
      if (error.request) {
        console.error('Request details:', JSON.stringify(error.request, null, 2));
      }
      
      throw error;
    }
  }

  async archiveDomains(domainNames) {
    try {
      const variables = {
        domain_names: domainNames,
      };

      const result = await this.client.request(ARCHIVE_DOMAINS, variables);
      return result.archiveDomains;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error archiving domains:', error);
      throw error;
    }
  }
}

export default BulkImportApi;
